/**
 * useBulkEnrollment Hook
 */

import { useState, useCallback } from 'react';
import { bulkEnroll, bulkCancelEnrollments } from '../../services/coursesService';
import type { BulkEnrollResult, BulkCancelResult } from '../../types/courses.types';

interface BulkEnrollActionResult {
  success: boolean;
  data?: BulkEnrollResult;
  error?: string;
}

interface BulkCancelActionResult {
  success: boolean;
  data?: BulkCancelResult;
  error?: string;
}

interface UseBulkEnrollmentResult {
  isEnrolling: boolean;
  isCancelling: boolean;
  enrollEmployees: (slug: string, employeeIds: number[]) => Promise<BulkEnrollActionResult>;
  cancelEnrollments: (enrollmentIds: number[]) => Promise<BulkCancelActionResult>;
}

export function useBulkEnrollment(employeeId: number | null): UseBulkEnrollmentResult {
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const enrollEmployees = useCallback(
    async (slug: string, employeeIds: number[]): Promise<BulkEnrollActionResult> => {
      if (!employeeId) return { success: false, error: 'Manglende parametre' };
      if (employeeIds.length === 0) return { success: false, error: 'Ingen medarbejdere valgt' };
      setIsEnrolling(true);
      try {
        const data = await bulkEnroll(slug, employeeIds);
        return { success: true, data };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Fejl ved tilmelding',
        };
      } finally {
        setIsEnrolling(false);
      }
    },
    [employeeId]
  );

  const cancelEnrollments = useCallback(
    async (enrollmentIds: number[]): Promise<BulkCancelActionResult> => {
      if (!employeeId) return { success: false, error: 'Manglende parametre' };
      if (enrollmentIds.length === 0) return { success: false, error: 'Ingen tilmeldinger valgt' };
      setIsCancelling(true);
      try {
        const data = await bulkCancelEnrollments(enrollmentIds);
        return { success: true, data };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Fejl ved afmelding',
        };
      } finally {
        setIsCancelling(false);
      }
    },
    [employeeId]
  );

  return { isEnrolling, isCancelling, enrollEmployees, cancelEnrollments };
}
